import { type SimulationInput, chanceFromMinima, simulateMinima } from './probability';

/**
 * Alvos sugeridos (WP-05). Da mesma simulação da chance (ADR-0005) saem três alvos, do mais seguro ao
 * mais ousado. Cada um é o menor preço "redondo" que ainda tem a chance pedida no horizonte.
 */
export type TargetKind = 'provavel' | 'bom_negocio' | 'ousado';

export interface SuggestedTarget {
  kind: TargetKind;
  target: number;
  /** 0–100, calculada para o valor já arredondado. */
  chance: number;
}

export interface TargetSuggestions {
  suggestions: SuggestedTarget[];
  horizonDays: number;
  confidence: 'baixa' | 'moderada' | 'alta';
}

/** Chance mínima de cada sugestão. */
export const TARGET_LEVELS: readonly { kind: TargetKind; chance: number }[] = [
  { kind: 'provavel', chance: 70 },
  { kind: 'bom_negocio', chance: 50 },
  { kind: 'ousado', chance: 25 },
];

/** Abaixo desta chance o app oferece um alvo com mais potencial. */
export const LOW_CHANCE = 30;

/** Passo de arredondamento: R$ 10 até mil, R$ 50 até dez mil, R$ 100 acima. */
export function roundingStep(price: number): number {
  if (price < 1000) return 10;
  if (price < 10_000) return 50;
  return 100;
}

export function suggestTargets(input: SimulationInput): TargetSuggestions | null {
  const sim = simulateMinima(input);
  if (!sim) return null;
  const n = sim.minima.length;
  const out: SuggestedTarget[] = [];
  for (const level of TARGET_LEVELS) {
    // Quantil dos mínimos: P(mínimo <= alvo) >= chance pedida.
    const idx = Math.min(n - 1, Math.max(0, Math.ceil((level.chance / 100) * n) - 1));
    const raw = Math.exp(sim.minima[idx]!);
    const step = roundingStep(raw);
    let target = Math.ceil(raw / step) * step;
    // Arredondar para cima nunca deve passar do preço de hoje: aí a sugestão não serve.
    if (target >= input.current) target = Math.floor((input.current - 1) / step) * step;
    if (target <= 0 || target >= input.current) continue;
    const chance = chanceFromMinima(sim.minima, target);
    if (out.some((s) => s.target === target)) continue;
    out.push({ kind: level.kind, target, chance });
  }
  return { suggestions: out, horizonDays: sim.horizonDays, confidence: sim.confidence };
}

/** Sugestão para quando o alvo atual tem chance baixa: o menor valor com pelo menos 50% de chance. */
export function betterTarget(
  current: { target: number; chance: number },
  suggestions: readonly SuggestedTarget[],
): SuggestedTarget | null {
  if (current.chance >= LOW_CHANCE) return null;
  const candidates = suggestions
    .filter((s) => s.chance >= 50 && s.target > current.target)
    .sort((a, b) => a.target - b.target);
  return candidates[0] ?? null;
}
